import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { AlertCircle, ChevronLeft, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { CreatorPicker } from "@/components/CreatorPicker"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { CategoryMultiSelect } from "@/components/CategoryMultiSelect"
import {
  CoverImageDrop,
  type CoverImageResult,
} from "@/components/CoverImageDrop"
import { VideoFileDrop, type UploadResult } from "@/components/VideoFileDrop"
import { useAllAlbums } from "@/lib/queries/albums"
import {
  useCreateChapter,
  useGetChapter,
  useUpdateChapter,
} from "@/lib/queries/chapters"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Chapter form (combined New + Edit)
// LEGACY: apps/admin/src/pages/AddChapter.jsx
// API:    POST /admin/chapter_get    { id }                      (when editing)
//         POST /admin/chapter_create { ...form }                 (when creating)
//         POST /admin/chapter_update { id, ...form (narrower) }  (when editing)
//
// ─── Field parity (legacy ↔ v2) ──────────────────────────────────────
// CREATE payload:
//   album_id              ← first selected album (legacy back-compat)
//   album_ids[]           ← multi-select album ids
//   title                 ← Title
//   author                ← Author
//   description           ← Description textarea
//   chapter_type          ← number (1=Free, 2=Paid)
//   S3_cover_image        ← cover upload s3response (capital S preserved)
//   cover_image_name      ← cover upload file_name
//   s3_data               ← VIDEO upload s3response
//   s30_data              ← VIDEO upload s3_30sec_data (30-sec preview)
//   file_name             ← VIDEO upload file_name
//   duration              ← auto-detected via DOM <video>/<audio> on metadata-load
//   is_notification       ← Switch (CREATE only in legacy UI)
//   notification_message  ← Textarea (only when is_notification true)
//   order                 ← number
//
// UPDATE payload — narrower (legacy hides video upload + duration on edit):
//   id, album_id?, album_ids[], title, author, S3_cover_image?, cover_image_name?,
//   description, chapter_type, order, is_notification?, notification_message?
//
// Validation parity:
//   - title required on CREATE (legacy: !title && !params.id)
//   - On EDIT, video upload + duration UI is HIDDEN (matches legacy)
//   - On EDIT, is_notification UI is HIDDEN (matches legacy; we still send
//     the existing values from the loaded record)
//   - Cover image is OPTIONAL on both — legacy didn't enforce it for chapters
//     (different from albums where cover is required on create)
// ───────────────────────────────────────────────────────────────────────

const schema = z.object({
  title: z.string().min(1, "Title is required"),
  author: z.string().optional(),
  description: z.string().optional(),
  chapter_type: z.coerce.number().int().min(1).max(2),
  album_ids: z.array(z.string()),
  is_notification: z.boolean(),
  notification_message: z.string().optional(),
  order: z.coerce.number().int().min(0),
  duration: z.union([z.string(), z.number()]).optional(),
  // V-Q5: carousel toggle (parity with album form).
  is_carousel: z.boolean(),
  carousel_order: z.coerce.number().int().min(0),
  // V-Q5: optional metadata.
  producer: z.string().max(120).optional().default(""),
  speaker: z.string().max(120).optional().default(""),
  curator: z.string().max(120).optional().default(""),
  publicly_available: z.boolean().default(false),
  // V-Q10: admin soft-hide toggle. Same semantic as album.
  is_hidden: z.boolean().default(false),
})

type FormValues = z.infer<typeof schema>

const DEFAULT_VALUES: FormValues = {
  title: "",
  author: "",
  description: "",
  chapter_type: 1,
  album_ids: [],
  // V-Q6: first-publish defaults to "notify followers." Admins can still
  // opt out via the toggle on CREATE. The toggle is hidden on EDIT and
  // `is_notification` is stripped from the EDIT payload below.
  is_notification: true,
  notification_message: "",
  order: 0,
  duration: "",
  // V-Q5: matches AlbumForm — default ON; admin can toggle off for
  // chapters they don't want surfaced in "New Releases".
  is_carousel: true,
  carousel_order: 0,
  producer: "",
  speaker: "",
  curator: "",
  publicly_available: false,
  is_hidden: false,
}

/**
 * Read media duration in seconds from a freshly-uploaded file URL.
 * Mirrors the legacy DOM-element approach.
 */
function readMediaDuration(url: string, isVideo: boolean): Promise<number | null> {
  return new Promise((resolve) => {
    const el = document.createElement(isVideo ? "video" : "audio")
    el.preload = "metadata"
    el.src = url
    el.onloadedmetadata = () => resolve(Number.isFinite(el.duration) ? el.duration : null)
    el.onerror = () => resolve(null)
    // Hard timeout so we don't block submit forever on a flaky CDN.
    setTimeout(() => resolve(null), 8000)
  })
}

export default function ChapterForm() {
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const navigate = useNavigate()

  const albumsQ = useAllAlbums()
  const chapterQ = useGetChapter(id)
  const createMut = useCreateChapter()
  const updateMut = useUpdateChapter()
  const submittingMut = isEdit ? updateMut : createMut

  const [coverResult, setCoverResult] = useState<CoverImageResult | null>(null)
  const [videoResult, setVideoResult] = useState<UploadResult | null>(null)
  const [isCoverUploading, setIsCoverUploading] = useState(false)
  const [isVideoUploading, setIsVideoUploading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  })

  const isNotification = watch("is_notification")

  // Hydrate when editing.
  useEffect(() => {
    if (!isEdit || !chapterQ.data) return
    const c = chapterQ.data
    const idsFromArray = Array.isArray(c.album_ids)
      ? (c.album_ids.filter((x): x is string => typeof x === "string") as string[])
      : []
    let albumIdsInit = idsFromArray
    if (albumIdsInit.length === 0) {
      const single =
        typeof c.album_id === "string"
          ? c.album_id
          : c.album_id && typeof c.album_id === "object"
            ? c.album_id._id
            : undefined
      if (single) albumIdsInit = [single]
    }

    reset({
      title: c.title ?? "",
      author: c.author ?? "",
      description: c.description ?? "",
      chapter_type:
        typeof c.chapter_type === "number"
          ? c.chapter_type
          : Number(c.chapter_type) === 2
            ? 2
            : 1,
      album_ids: albumIdsInit,
      is_notification: !!c.is_notification,
      notification_message: c.notification_message ?? "",
      order: typeof c.order === "number" ? c.order : 0,
      duration:
        typeof c.duration === "number" || typeof c.duration === "string"
          ? c.duration
          : "",
      // V-Q5: legacy chapters without the field default to true (matches
      // schema + the runtime filter's `{ $ne: false }` rule).
      is_carousel: c.is_carousel === false ? false : true,
      carousel_order:
        typeof c.carousel_order === "number" ? c.carousel_order : 0,
      producer: c.producer ?? "",
      speaker: c.speaker ?? "",
      curator: c.curator ?? "",
      publicly_available: !!c.publicly_available,
      is_hidden: !!c.is_hidden,
    })
  }, [isEdit, chapterQ.data, reset])

  // When a new file uploads (CREATE flow OR EDIT replacement), auto-fill
  // duration via DOM media element. Used to skip on edit; now runs in
  // both modes so the "replace audio file" path on EDIT writes the new
  // durationInSec the same way create does.
  useEffect(() => {
    if (!videoResult) return
    const url = (videoResult.s3response as { Location?: string })?.Location
    if (!url) return
    const lower = (videoResult.file_name || "").toLowerCase()
    const isVideo = /\.(mp4|mov|m4v|webm|mkv|avi|wmv)$/i.test(lower)
    void readMediaDuration(url, isVideo).then((d) => {
      if (d != null) setValue("duration", d, { shouldDirty: true })
    })
  }, [videoResult, setValue])

  // Map AdminAlbum-shape to the {_id, name} shape CategoryMultiSelect expects.
  const albumOptionsForChips = useMemo(
    () => (albumsQ.data ?? []).map((a) => ({ _id: a._id, name: a.title })),
    [albumsQ.data]
  )

  const existingCoverPreview = isEdit ? chapterQ.data?.cover_image : undefined

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)

    if (!isEdit && !videoResult) {
      setSubmitError("Upload a video or audio file first.")
      return
    }

    const firstAlbumId = values.album_ids[0]

    try {
      if (isEdit && id) {
        const payload: Parameters<typeof updateMut.mutateAsync>[0] = {
          id,
          album_id: firstAlbumId,
          album_ids: values.album_ids,
          title: values.title,
          author: values.author ?? "",
          description: values.description ?? "",
          chapter_type: values.chapter_type,
          order: values.order,
          // V-Q6: notification fields are CREATE-only. Stripping them on
          // EDIT means re-saving an existing chapter cannot re-page
          // followers. The cron's is_notified idempotency backs this up
          // server-side, but we strip client-side too so the intent is
          // explicit. (Legacy admin re-sent them; we deliberately do not.)
          is_carousel: values.is_carousel,
          carousel_order: values.carousel_order,
          producer: values.producer ?? "",
          speaker: values.speaker ?? "",
          curator: values.curator ?? "",
          publicly_available: values.publicly_available,
          is_hidden: values.is_hidden,
        }
        // V-Q5: optional audio replacement on edit. When a fresh
        // upload is attached we send s3_data + s30_data + duration so
        // the backend swaps video_url / shareUrl / durationInSec.
        // Absent → backend leaves the existing media untouched.
        if (videoResult?.s3response) {
          payload.s3_data = videoResult.s3response
          payload.s30_data = videoResult.s3_30sec_data
          payload.file_name = videoResult.file_name
          payload.duration = values.duration
        }
        if (coverResult?.s3response) {
          payload.S3_cover_image = coverResult.s3response
          payload.cover_image_name = coverResult.file_name
        }
        const res = await updateMut.mutateAsync(payload)
        if (!res?.success) {
          setSubmitError(res?.message || "Failed to update chapter.")
          return
        }
      } else {
        const res = await createMut.mutateAsync({
          album_id: firstAlbumId,
          album_ids: values.album_ids,
          title: values.title,
          author: values.author ?? "",
          description: values.description ?? "",
          chapter_type: values.chapter_type,
          S3_cover_image: coverResult?.s3response,
          cover_image_name: coverResult?.file_name,
          s3_data: videoResult?.s3response,
          s30_data: videoResult?.s3_30sec_data,
          file_name: videoResult?.file_name,
          duration: values.duration,
          is_notification: values.is_notification,
          notification_message: values.is_notification
            ? values.notification_message ?? ""
            : "",
          order: values.order,
          is_carousel: values.is_carousel,
          carousel_order: values.carousel_order,
          producer: values.producer ?? "",
          speaker: values.speaker ?? "",
          curator: values.curator ?? "",
          publicly_available: values.publicly_available,
          is_hidden: values.is_hidden,
        })
        if (!res?.success) {
          setSubmitError(res?.message || "Failed to create chapter.")
          return
        }
      }
      navigate("/chapters")
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : `Failed to ${isEdit ? "update" : "create"} chapter.`
      )
    }
  })

  const isLoadingPrefill = isEdit && chapterQ.isLoading

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <a href="/chapters">
            <ChevronLeft className="mr-1 h-4 w-4" /> Back to chapters
          </a>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? "Edit chapter" : "Create chapter"}</CardTitle>
          <CardDescription>
            {isEdit
              ? chapterQ.data?.title || "Loading…"
              : "Upload the video first; duration is auto-detected."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEdit && chapterQ.isError && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load this chapter. Try refreshing.</span>
            </div>
          )}

          {isLoadingPrefill ? (
            <div className="grid h-32 place-items-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <form className="space-y-6" onSubmit={onSubmit} noValidate>
              {/* Albums multi-select */}
              <div className="space-y-2">
                <Label>Albums</Label>
                <Controller
                  control={control}
                  name="album_ids"
                  render={({ field }) => (
                    <CategoryMultiSelect
                      options={albumOptionsForChips}
                      value={field.value}
                      onChange={field.onChange}
                      loading={albumsQ.isLoading}
                      disabled={submittingMut.isPending}
                    />
                  )}
                />
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">
                  Title<span className="text-destructive"> *</span>
                </Label>
                <Input
                  id="title"
                  aria-invalid={!!errors.title}
                  {...register("title")}
                />
                {errors.title && (
                  <p className="text-xs text-destructive">{errors.title.message}</p>
                )}
              </div>

              {/* Cover image */}
              <div className="space-y-2">
                <Label>Cover image</Label>
                <CoverImageDrop
                  initialPreview={existingCoverPreview}
                  result={coverResult}
                  onResult={setCoverResult}
                  onUploadingChange={setIsCoverUploading}
                  disabled={submittingMut.isPending}
                />
              </div>

              {/* Author — picker against the Creators directory */}
              <div className="space-y-2">
                <Label htmlFor="author">Author</Label>
                <Controller
                  control={control}
                  name="author"
                  render={({ field }) => (
                    <CreatorPicker
                      id="author"
                      value={field.value || ""}
                      onChange={field.onChange}
                      placeholder="Pick or create the author"
                    />
                  )}
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  rows={4}
                  {...register("description")}
                />
              </div>

              {/* Chapter type */}
              <div className="space-y-2">
                <Label htmlFor="chapter_type">Chapter type</Label>
                <Controller
                  control={control}
                  name="chapter_type"
                  render={({ field }) => (
                    <Select
                      value={String(field.value)}
                      onValueChange={(v) => field.onChange(Number(v))}
                    >
                      <SelectTrigger id="chapter_type" className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Free</SelectItem>
                        <SelectItem value="2">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              {/* Video / audio file — required on create, optional on
                  edit (replacement only). Leaving this blank on edit
                  preserves the existing media; uploading a new file
                  swaps video_url / shareUrl / durationInSec on save. */}
              <div className="space-y-2">
                <Label>
                  {isEdit ? "Replace audio file" : (
                    <>
                      Video / audio file
                      <span className="text-destructive"> *</span>
                    </>
                  )}
                </Label>
                <VideoFileDrop
                  result={videoResult}
                  onResult={setVideoResult}
                  onUploadingChange={setIsVideoUploading}
                  disabled={submittingMut.isPending}
                />
                {isEdit && (
                  <p className="text-xs text-muted-foreground">
                    Optional. Leave empty to keep the existing audio.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration">Duration (seconds)</Label>
                <Input
                  id="duration"
                  placeholder={
                    isEdit
                      ? "Auto-filled when a new file is uploaded"
                      : "Auto-filled when video is uploaded"
                  }
                  {...register("duration")}
                />
              </div>

              {/* CREATE-only: notification */}
              {!isEdit && (
                <>
                  <div className="space-y-3 rounded-md border p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <Label htmlFor="is_notification">Send notification</Label>
                        <p className="text-xs text-muted-foreground">
                          Push to subscribed users when this chapter goes live.
                        </p>
                      </div>
                      <Controller
                        control={control}
                        name="is_notification"
                        render={({ field }) => (
                          <Switch
                            id="is_notification"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        )}
                      />
                    </div>
                    {isNotification && (
                      <div className="space-y-2">
                        <Label htmlFor="notification_message">
                          Notification message
                        </Label>
                        <Textarea
                          id="notification_message"
                          rows={3}
                          {...register("notification_message")}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* V-Q5: Carousel toggle (parity with album form). Default
                  ON; admin can toggle off to hide a chapter from the
                  Discover "New Releases" rail without unpublishing. */}
              <div className="flex items-center justify-between gap-4 rounded-md border p-4">
                <div>
                  <Label htmlFor="is_carousel">Show in carousel</Label>
                  <p className="text-xs text-muted-foreground">
                    Featured chapters appear in Discover &rarr; New
                    Releases.
                  </p>
                </div>
                <Controller
                  control={control}
                  name="is_carousel"
                  render={({ field }) => (
                    <Switch
                      id="is_carousel"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>

              {/* V-Q10: soft-hide toggle. Hides the chapter from
                  every user-facing surface while keeping it in admin
                  lists. Reversible. */}
              <div className="flex items-center justify-between gap-4 rounded-md border p-4">
                <div>
                  <Label htmlFor="is_hidden">Hide from users</Label>
                  <p className="text-xs text-muted-foreground">
                    Keeps this chapter out of the app while
                    work-in-progress. Toggle off to publish back.
                  </p>
                </div>
                <Controller
                  control={control}
                  name="is_hidden"
                  render={({ field }) => (
                    <Switch
                      id="is_hidden"
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>

              {/* V-Q5: Metadata block (parity with album form). */}
              <div className="space-y-3 rounded-md border p-4">
                <div>
                  <Label className="text-base">Metadata</Label>
                  <p className="text-xs text-muted-foreground">
                    Optional. Shown on the mobile info sheet — leave a
                    field blank to hide it.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="producer">Producer</Label>
                    <Input id="producer" placeholder="Area52" {...register("producer")} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="speaker">Speaker</Label>
                    <Input id="speaker" placeholder="Bob Lazar" {...register("speaker")} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="curator">Curator</Label>
                    <Input id="curator" placeholder="GoodTalk team" {...register("curator")} />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor="publicly_available">Publicly available</Label>
                    <p className="text-xs text-muted-foreground">
                      Show a "publicly available" indicator on the info
                      sheet.
                    </p>
                  </div>
                  <Controller
                    control={control}
                    name="publicly_available"
                    render={({ field }) => (
                      <Switch
                        id="publicly_available"
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Order + Carousel order */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    min={0}
                    {...register("order")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="carousel_order">Carousel order</Label>
                  <Input
                    id="carousel_order"
                    type="number"
                    min={0}
                    {...register("carousel_order")}
                  />
                </div>
              </div>

              {submitError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  disabled={
                    isCoverUploading || isVideoUploading || submittingMut.isPending
                  }
                >
                  {(submittingMut.isPending ||
                    isCoverUploading ||
                    isVideoUploading) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isVideoUploading
                    ? "Uploading video…"
                    : isCoverUploading
                      ? "Uploading cover…"
                      : submittingMut.isPending
                        ? isEdit
                          ? "Saving…"
                          : "Creating…"
                        : isEdit
                          ? "Update chapter"
                          : "Create chapter"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/chapters")}
                  disabled={submittingMut.isPending}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
